/*
  This is used to preview a saved section.
*/

import { useState, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import CustomDialog from 'components/CustomDialog/customDialog';
import { setDialog } from 'store/actions/dialogActions';
import selectors from 'store/selectors';
import DIALOGS from 'utils/dialogIds';

import useTemplateBuilder from 'utils/CustomHooks/templateBuilder/useTemplateBuilder';
import { DialogHeader } from 'common/styles/main';
import CloseIcon from 'components/Icons/closeIcon';
import LaptopIcon from 'components/Icons/LaptopIcon';
import MobileIcon from 'components/Icons/mobileIcon';
import { DialogContent, ToggleWrapper } from './style';
import TemplateBody from 'components/TemplatePreview/TemplateBody';

// This is used to preview a saved section.
const SectionPreview = () => {
  const dispatch = useDispatch();
  const dialogState = useSelector(selectors.getDialogState);
  const { open, data } = dialogState?.[DIALOGS?.SECTION_PREVIEW] || {};
  const { sectionId } = data || {};

  // This is used to store the section data.
  const [section, setSection] = useState<any>(null);

  const {
    getSavedSection,
    organizeChecklistIntoHierarchy,
    organizeChecklistIntoLinear,
  } = useTemplateBuilder();

  const [viewType, setViewType] = useState('WEB');

  const handleClose = useCallback(() => {
    dispatch(setDialog({ open: false, dialogId: DIALOGS.SECTION_PREVIEW }));
  }, [dispatch]);

  // This is used to get the section data.
  const getSection = async () => {
    const response = await getSavedSection(sectionId);
    const hierarchyItems = organizeChecklistIntoHierarchy(response?.data ?? {});
    const linearItems = organizeChecklistIntoLinear(response?.data ?? {});
    setSection({ ...response?.data, hierarchyItems, linearItems });
  };

  useEffect(() => {
    getSection();
  }, []);

  return (
    <CustomDialog
      hideActions
      dialogProps={{
        open,
        maxWidth: false,
        onClose: handleClose,
        sx: {
          borderRadius: '24px',
          '& .MuiDialog-paper': {
            maxWidth: '850px',
          },
          '& .MuiDialogContent-root': {
            maxWidth: '100%',
            width: '850px',
          },
        },
      }}
    >
      <>
        <DialogHeader>
          <Typography className="title">Section Preview</Typography>
          <IconButton className={'closeIcon'} onClick={handleClose}>
            <CloseIcon sx={{ fontSize: '13px' }} />
          </IconButton>
        </DialogHeader>

        <DialogContent
          sx={{
            ...(viewType === 'WEB' && {
              maxHeight: 'unset',
            }),
          }}
        >
          <ToggleWrapper>
            <ToggleButtonGroup
              color="primary"
              sx={{ color: '#424242' }}
              value={viewType}
              exclusive
              onChange={(e: any, value) => {
                if (value) {
                  setViewType(value);
                }
              }}
              aria-label="View toggle"
            >
              <ToggleButton
                sx={{ padding: '6px 8px', fontWeight: 600 }}
                value="WEB"
              >
                <LaptopIcon
                  color={viewType === 'WEB' ? '#6868FE' : '#757575'}
                  sx={{ fill: 'none', fontSize: 20, mr: '4px' }}
                />
                Web
              </ToggleButton>
              <ToggleButton
                sx={{ padding: '6px 8px', fontWeight: 600 }}
                value="MOBILE"
              >
                <MobileIcon
                  color={viewType === 'MOBILE' ? '#6868FE' : '#757575'}
                  sx={{ fill: 'none', fontSize: 20, mr: '4px' }}
                />
                Mobile
              </ToggleButton>
            </ToggleButtonGroup>
          </ToggleWrapper>

          {/* This is used to render the mobile view of the section. */}
          {section && viewType === 'MOBILE' && (
            <TemplateBody checklistItems={section?.hierarchyItems} />
          )}

          {/* This is used to render the web view of the section. */}
          {section && viewType === 'WEB' && (
            <TemplateBody checklistItems={section?.hierarchyItems} />
          )}
        </DialogContent>
      </>
    </CustomDialog>
  );
};

export default SectionPreview;
